import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { FaQuoteLeft } from "react-icons/fa";
import { REACT_APP_API_URL } from "../env";
import SlimEvent from "../components/SlimEvent";
import styles from "./Events.module.css";

const API_BASE = REACT_APP_API_URL.replace(/\/$/, "");

const resolveAssetUrl = (assetPath) => {
  if (!assetPath) return "";
  if (/^(https?:|data:|blob:)/i.test(assetPath)) return assetPath;
  if (assetPath.startsWith("/eventUploads/")) return `${API_BASE}${assetPath}`;
  return "";
};

const renderText = (text) =>
  String(text || "")
    .split("\n")
    .map((line, index) => (
      <React.Fragment key={`${line}-${index}`}>
        {line}
        {index < String(text || "").split("\n").length - 1 && <br />}
      </React.Fragment>
    ));

const EventHero = ({ page }) => (
  <section
    className={styles.hero}
    style={{
      backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.35), rgba(0, 0, 0, 0.45)), url('${resolveAssetUrl(
        page.heroImage
      )}')`,
    }}
    aria-label={page.heroAlt || page.title}
  >
    <h1>{page.title || "EVENTS"}</h1>
  </section>
);

const FeatureSection = ({ event }) => (
  <section className={`${styles.featureSection} container-fluid py-5`}>
    <div className={`${styles.featureWrapper} mx-auto`}>
      <div className="row align-items-center g-4">
        <div className="col-lg-6">
          <div className={styles.imageStack}>
            {event.primaryImage && (
              <img
                src={resolveAssetUrl(event.primaryImage)}
                alt={event.imageAlt || event.title}
                className={styles.imageMain}
              />
            )}
            {event.secondaryImage && (
              <img
                src={resolveAssetUrl(event.secondaryImage)}
                alt={event.secondaryImageAlt || event.title}
                className={styles.imageOverlay}
              />
            )}
          </div>
        </div>

        <div className="col-lg-6">
          <div className={styles.contentBox}>
            <h2 className={styles.featureTitle}>{event.title}</h2>
            {event.subtitle && <p className={styles.sectionSubtitle}>{event.subtitle}</p>}

            {(event.paragraphs || []).map((paragraph, index) => (
              <p
                className={`${styles.eventText} ${index === event.paragraphs.length - 1 ? styles.highlight : ""
                  }`}
                key={`${event.intEventID}-paragraph-${index}`}
              >
                {renderText(paragraph)}
              </p>
            ))}

            {event.tags?.length > 0 && (
              <div className={styles.eventTags}>
                {event.tags.map((tag) => (
                  <span key={tag}>#{tag}</span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  </section>
);

const ArticleSection = ({ event }) => (
  <section className={styles.articleSection}>
    <div className="text-center mb-4">
      <h2 className="fw-bold">{event.title}</h2>
      {event.subtitle && <p className="text-muted fs-5">{event.subtitle}</p>}
    </div>

    <div className={styles.articleBody}>
      {(event.paragraphs || []).map((paragraph, index) => (
        <p key={`${event.intEventID}-article-${index}`}>{renderText(paragraph)}</p>
      ))}

      {event.linkUrl && (
        <p>
          <a
            href={event.linkUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-decoration-none fw-semibold"
          >
            {event.linkLabel || event.linkUrl}
          </a>
        </p>
      )}
    </div>

    {event.primaryImage && (
      <div className={styles.articleImageWrap}>
        <img
          src={resolveAssetUrl(event.primaryImage)}
          alt={event.imageAlt || event.title}
          className="img-fluid rounded w-100"
        />
        {event.imageCaption && (
          <div className={styles.imageCaption}>{event.imageCaption}</div>
        )}
      </div>
    )}
  </section>
);

const CardGridSection = ({ event }) => (
  <section className={styles.cardGridSection}>
    <div className="text-center mb-5">
      <h2 className="fw-bold">{event.title}</h2>
      {event.subtitle && <p className="text-muted">{event.subtitle}</p>}
    </div>

    {event.cards?.length > 0 && (
      <div className={styles.cardsContainer}>
        {event.cards.map((card, index) => (
          <div
            className={styles.card}
            style={{ backgroundImage: `url(${resolveAssetUrl(card.image)})` }}
            key={`${event.intEventID}-card-${index}`}
          >
            <div className={styles.cardOverlay}></div>
            <div className={styles.cardContent}>
              <h3 className={styles.cardTitle}>{card.title}</h3>
              {card.description && (
                <p className={styles.cardDescription}>{card.description}</p>
              )}
            </div>
          </div>
        ))}
      </div>
    )}

    <div className="container py-4">
      {(event.paragraphs || []).map((paragraph, index) => (
        <p className="mb-3" key={`${event.intEventID}-grid-${index}`}>
          {renderText(paragraph)}
        </p>
      ))}
    </div>
  </section>
);

const BannerSection = ({ event }) => (
  <section
    className={styles.bannerSection}
    style={{
      backgroundImage: `url(${resolveAssetUrl(event.primaryImage)})`,
    }}
    aria-label={event.imageAlt || event.title}
  >
    <div className={styles.bannerOverlay}></div>
    <div className={styles.bannerContent}>
      {event.eventDate && <p className={styles.bannerDate}>{event.eventDate}</p>}
      <h2 className={styles.bannerTitle}>
        {event.title}
        {event.subtitle && <span>{event.subtitle}</span>}
      </h2>
      {event.location && <p className={styles.bannerLocation}>{event.location}</p>}
    </div>
  </section>
);

const QuoteSection = ({ event }) => (
  <section className={styles.quoteSection}>
    <div className={styles.quoteContent}>
      <FaQuoteLeft className={styles.quoteIcon} />
      <blockquote className={styles.quote}>
        {event.quote}
        {event.quoteAuthor && <strong> - {event.quoteAuthor}</strong>}
      </blockquote>
    </div>
    {event.primaryImage && (
      <div className={styles.quoteImageWrapper}>
        <img
          src={resolveAssetUrl(event.primaryImage)}
          alt={event.imageAlt || event.title}
          className={styles.quoteImage}
        />
      </div>
    )}
  </section>
);

const EventSection = ({ event }) => {
  switch (event.sectionType) {
    case "event":
      return <SlimEvent />;
    case "feature":
      return <FeatureSection event={event} />;
    case "card-grid":
      return <CardGridSection event={event} />;
    case "banner":
      return <BannerSection event={event} />;
    case "quote":
      return <QuoteSection event={event} />;
    case "article":
    default:
      return <ArticleSection event={event} />;
  }
};

function Events() {
  const [page, setPage] = useState({ title: "EVENTS", heroImage: "" });
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let ignore = false;

    const loadEvents = async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/events`);
        if (ignore) return;

        setPage(res.data.page || {});
        setEvents(res.data.data || []);
        setError("");
      } catch (err) {
        if (!ignore) {
          setError("Events are unavailable right now.");
        }
      } finally {
        if (!ignore) setLoading(false);
      }
    };

    loadEvents();

    return () => {
      ignore = true;
    };
  }, []);

  const featuredEvents = useMemo(
    () => events.filter((event) => event.featured || event.sectionType === "feature"),
    [events]
  );
  const gridEvents = useMemo(
    () => events.filter((event) => !featuredEvents.includes(event)),
    [events, featuredEvents]
  );

  return (
    <>
      <EventHero page={page} />

      <div className={styles.appContainer}>
        {loading && <div className={styles.stateMessage}>Loading events...</div>}
        {!loading && error && <div className={styles.stateMessage}>{error}</div>}
        {!loading && !error && events.length === 0 && (
          <div className={styles.stateMessage}>No events are available.</div>
        )}

        {!loading && !error && <SlimEvent />}

        {!loading &&
          !error &&
          featuredEvents.map((event) => (
            <EventSection event={event} key={event.intEventID} />
          ))}

        {!loading && !error && gridEvents.length > 0 && (
          <main className={styles.mainGrid}>
            {gridEvents.map((event) => (
              <EventSection event={event} key={event.intEventID} />
            ))}
          </main>
        )}
      </div>
    </>
  );
}

export default Events;
